'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import {
  mergeClickIdIntoLastTouch,
  setFirstTouchOnce,
  setLastTouch,
  type AttributionData,
} from '@/utils/attribution'
import {
  CLICK_ID_COOKIES,
  CLICK_ID_KINDS,
  CLICK_ID_TTL_DAYS,
} from '@/lib/tracking/config'

/** Drop self-referrals — an internal navigation reports our own host as the
 *  referrer, which is not a real acquisition source. */
function cleanReferrer(): string | undefined {
  const raw = document.referrer || undefined
  if (!raw) return undefined
  try {
    return new URL(raw).host === window.location.host ? undefined : raw
  } catch {
    return undefined
  }
}

export function useAttributionCapture() {
  const searchParams = useSearchParams()

  useEffect(() => {
    const source = searchParams.get('utm_source') ?? undefined
    const medium = searchParams.get('utm_medium') ?? undefined
    const campaign = searchParams.get('utm_campaign') ?? undefined
    const content = searchParams.get('utm_content') ?? undefined
    const term = searchParams.get('utm_term') ?? undefined
    // utm_id carries Meta's `{{campaign.id}}` macro (live on the ad account
    // since 2026-09-03) and populates GA4's Manual campaign ID dimension, which
    // is the join key back to Meta's own reporting.
    const id = searchParams.get('utm_id') ?? undefined
    const gclid = searchParams.get('gclid') ?? undefined
    const gbraid = searchParams.get('gbraid') ?? undefined
    const wbraid = searchParams.get('wbraid') ?? undefined
    const fbclid = searchParams.get('fbclid') ?? undefined
    const referrer = cleanReferrer()

    // Persist whichever Google click id the URL carries into its durable cookie
    // so it survives later non-ad return visits — the single source for the
    // offline Google Ads upload. iOS ad clicks send gbraid/wbraid, not gclid.
    const clickIds = { gclid, gbraid, wbraid }
    for (const kind of CLICK_ID_KINDS) {
      const value = clickIds[kind]
      if (!value) continue
      const expiryDate = new Date()
      expiryDate.setDate(expiryDate.getDate() + CLICK_ID_TTL_DAYS)
      document.cookie = `${CLICK_ID_COOKIES[kind]}=${encodeURIComponent(value)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax; Secure`
    }

    // Two tiers of arrival signal, deliberately NOT equivalent.
    //
    // A utm_* tag is an explicit campaign declaration and fully replaces
    // last-touch. A bare click id is only an identifier: Meta appends fbclid to
    // every Facebook/Instagram click including untagged organic posts, and
    // treating that as a campaign would overwrite the real source with a record
    // whose campaign fields are all undefined. Those two cases get different
    // writers — see mergeClickIdIntoLastTouch.
    //
    // A bare internal navigation carries neither and must never advance
    // last-touch at all.
    const hasUtmTag = Boolean(
      source || medium || campaign || content || term || id
    )
    const hasClickId = Boolean(gclid || gbraid || wbraid || fbclid)

    const data: AttributionData = {
      source,
      medium,
      campaign,
      content,
      term,
      id,
      gclid,
      gbraid,
      wbraid,
      fbclid,
      referrer,
      landing_page: window.location.pathname,
      timestamp: new Date().toISOString(),
    }

    // First-touch records the very first arrival (set once, never clobbered) —
    // including a pure direct visit, where landing page + timestamp alone are
    // the record. Without that seed, a direct-first visitor who later clicks a
    // tagged link would have the tagged visit misrecorded as their first touch.
    setFirstTouchOnce(data)
    if (hasUtmTag) {
      setLastTouch(data)
    } else if (hasClickId) {
      mergeClickIdIntoLastTouch(data)
    }
  }, [searchParams])
}
